import { Channel, ChannelType, EspBinding } from 'api/ChannelsAPI';
import { MessageMedium } from 'constants/messageMediums';
import { MessageType } from 'api/MessageTypeAPI';
import {
  BindingError,
  BindingTypes,
  ChannelBinding,
  MessageTypesByChannelId,
} from 'modules/channels/types';
import { EspAccount } from 'modules/graphql/types';
import { Option } from 'ui/radioList/types';
import { BaseOption } from 'ui/selects';
import { sortObjectListByKey, sumObjectListByKey } from 'utils/arrayHelpers';
import { ID_PREFIX } from '../constants';

type CreateOptionsListParams = {
  accounts: EspAccount[];
  options?: ChannelBinding[];
};

export const createOptionsList = ({ accounts, options }: CreateOptionsListParams): BaseOption[] => {
  const selectedOptions = options?.map((option) => option.option);
  return accounts.map(({ id, name, isDefault }) => ({
    badgeText: isDefault ? 'Default' : undefined,
    id: id.toString(),
    isDisabled: selectedOptions?.includes(id.toString()),
    label: name,
    value: id.toString(),
  }));
};

export const calculatePercentage = (selections: ChannelBinding[]) =>
  sumObjectListByKey(
    selections.map((item) => ({ ...item, num: parseInt(item.num, 10) })),
    'num',
  );

export const convertToEspBindings = (selected: ChannelBinding[]): EspBinding[] =>
  selected.map(({ num, option }) => ({
    espAccountId: parseInt(option, 10),
    trafficPercentage: parseInt(num, 10),
  }));

export const createDefaultChannelBindings = (espBindings: EspBinding[] = []): ChannelBinding[] => {
  if (espBindings.length === 0) {
    // create default option to get started with
    return [
      {
        id: `${ID_PREFIX}0`,
        num: '100',
        option: '',
      },
    ];
  }

  // map over exising bindings and create options
  return espBindings.map((binding, index) => {
    return {
      id: `${ID_PREFIX}${index}`,
      num: binding.trafficPercentage.toString(),
      option: binding.espAccountId.toString(),
    };
  });
};

export const createChannelBindingOptions = (
  accounts: EspAccount[],
  account: EspAccount | undefined,
): Option[] => {
  const prefixText = 'Use the default account, which is currently set to';
  const accountName =
    account?.name && account?.id ? `${account.name} (${account.id})` : 'Iterable Shared Pool';

  return [
    {
      id: 'default',
      name: `${prefixText} ${accountName}`,
      value: BindingTypes.DEFAULT,
    },
    {
      id: 'custom',
      isDisabled: accounts?.length === 0,
      name: 'Use third-party account',
      value: BindingTypes.CUSTOM,
    },
  ];
};

export const isEmailMarketingChannel = (channel: Channel) =>
  channel.messageMedium === MessageMedium.Email && channel.channelType === ChannelType.Marketing;

export const isFrequencyCapChannelV2 = (channel: Channel) =>
  [MessageMedium.Email, MessageMedium.SMS, MessageMedium.Push].includes(channel.messageMedium) &&
  channel.channelType === ChannelType.Marketing;

export const sortMessageTypes = (types: MessageType[] = []) =>
  types.sort((a, b) => a.name.localeCompare(b.name));

export const sortChannels = (channels: Channel[]) => sortObjectListByKey(channels, 'name');

/**
 * Filter channels based on name/id of channel or associated message types
 */
export const filterChannels = ({
  channels,
  messageTypes,
  searchValue = '',
}: {
  channels: Channel[];
  messageTypes: MessageTypesByChannelId;
  searchValue: string;
}) => {
  if (channels.length === 0) {
    return [];
  }

  const reducedChannels = channels.reduce((acc, current) => {
    const isChannelName = current.name.toLowerCase().includes(searchValue.toLowerCase());
    const isChannelId = current.id.toString().includes(searchValue); // numbers to strings
    if (isChannelName || isChannelId) {
      return [...acc, current];
    }
    const types = messageTypes[current.id] || [];
    const isMessageTypeNameOrId = types.some(
      (type) =>
        type.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        type.id.toString().includes(searchValue),
    );

    if (isMessageTypeNameOrId) {
      return [...acc, current];
    }

    return acc;
  }, [] as Channel[]);

  return reducedChannels;
};

export const createUniqueId = (list: string[], prefix: string, num: number): string => {
  const uniqueId = `${prefix}${num}`;
  return list.includes(uniqueId) ? createUniqueId(list, prefix, num + 1) : uniqueId;
};

export const createBindingsError = (bindings: BindingError) => ({
  accounts: bindings?.option?.message || '',
  percentages: bindings?.num?.message || '',
});
